import { AfterViewInit, Component, HostListener, inject, OnInit, signal } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TakeUntilDestroy } from '@app/shared/decorators/take-until-destroy.decorator';
import { ChallengesStatuses, LimitedTimeEvent } from '@app/shared/models/limitedTimeEvent.model';
import { Modal, ModalType, NonPersistedModalName } from '@app/shared/models/modal.model';
import { LoggerService } from '@app/shared/services/logger.service';
import { EventManager } from '@app/shared/utils/event-manager';
import { TranslateService } from '@ngx-translate/core';
import { finalize, takeUntil } from 'rxjs/operators';
import { AssetsService } from '@app/shared/services/assets.service';
import { LimitedTimeEventService } from '@app/shared/services/limitedTimeEvent.service';
import { NativeService } from '@app/shared/services/native.service';
import { AppService } from '@app/app.service';
import { Observable } from 'rxjs';
import { WalletComponent } from '@app/shared/components/wallet/wallet.component';
import { ProgressBarComponent } from '@app/shared/components/progress-bar/progress-bar.component';
import { DisplayComponent } from '@app/shared/components/display/display.component';
import { ButtonComponent } from '@app/shared/components/button/button.component';
import { BoxComponent } from '@app/shared/components/liveops/box/box.component';
import { AnimatedTimerComponent } from '@app/shared/components/animated-timer/animated-timer.component';
import { SharedModule } from '@app/shared/shared.module';
import { EventsRepository } from '@app/shared/state/events/events.repository';
import { Throttle } from '@app/shared/decorators/throttle.decorator';

@TakeUntilDestroy
@Component({
  selector: 'slotcom-multi-gamechallenges-modal',
  templateUrl: './multi-gamechallenges.component.html',
  styleUrls: ['./multi-gamechallenges.component.scss'],
  standalone: true,
  imports: [
    WalletComponent,
    ProgressBarComponent,
    DisplayComponent,
    ButtonComponent,
    BoxComponent,
    AnimatedTimerComponent,
    SharedModule,
  ],
})
export class MultiGameChallengesModalComponent implements OnInit, AfterViewInit {
  @HostListener('window:resize') onResizeWindow() {
    this.checkArrows();
  }

  componentDestroy: () => Observable<unknown>;

  private _gameChallengeCopy: LimitedTimeEvent;

  gameChallenge = signal<LimitedTimeEvent | null>(null);
  lang: string;
  showArrows = signal(false);
  gameIcon: unknown[] = [];
  challengeText: string[] = [];
  showCompleted: boolean;
  showTimer = signal(false);
  redeemingIds = signal(new Set<string>());

  private readonly eventManager = inject(EventManager);
  private readonly assetsService = inject(AssetsService);
  private readonly eventsRepo = inject(EventsRepository);
  private readonly translateService = inject(TranslateService);
  private readonly limitedTimeEventService = inject(LimitedTimeEventService);
  private readonly nativeService = inject(NativeService);
  private readonly appService = inject(AppService);
  private readonly logger = inject(LoggerService);

  gameChallengeModalData = inject<Modal>(MAT_DIALOG_DATA);

  get multiChallengesElementSelector() {
    return document.querySelector('.c-multichallenges__events');
  }

  ngAfterViewInit(): void {
    this.checkArrows();
  }

  ngOnInit(): void {
    this.lang = this.translateService.currentLang;

    this.eventsRepo.visibleLimitedTimeEvents$.pipe(takeUntil(this.componentDestroy())).subscribe(() => {
      const event = this.eventsRepo.getEventById(this.gameChallengeModalData.data.eventId);

      if (event) {
        this._gameChallengeCopy = event;
      }

      this.gameChallenge.set(this._gameChallengeCopy);
      this.checkArrows();
    });

    this.assetsService.gamesVersion$.subscribe({
      next: (versions) => {
        this.gameChallenge()?.properties.challengestatuses.forEach((challenge) => {
          this.gameIcon.push({
            'background-image': `url('${this.assetsService.getGameIconUrlLegacy(challenge.gameId, versions)}')`,
          });
          this.manageChallengeText(challenge);
        });
      },
      error: (error) => this.logger.error(error),
    });

    setTimeout(() => {
      if (this.gameChallenge()?.endDate) {
        this.showTimer.set(true);
      }
    }, 500);
  }

  @Throttle(2000)
  goToGame(gameId: string): void {
    this.eventManager
      .closeAllModals()
      .then(() => this.appService.showGame(gameId))
      .catch((e) => this.logger.error(e));
  }

  manageEvent(challengesStatus: ChallengesStatuses): void {
    if (challengesStatus.finished && !challengesStatus.redeemed) {
      if (this.redeemingIds().has(challengesStatus.id)) return;

      this.redeemingIds.update((s) => new Set([...s, challengesStatus.id]));

      this.limitedTimeEventService
        .redeemGameChallenge(this.gameChallenge()!.eventId, challengesStatus.id)
        .pipe(
          takeUntil(this.componentDestroy()),
          finalize(() =>
            this.redeemingIds.update((s) => {
              const n = new Set(s);
              n.delete(challengesStatus.id);
              return n;
            })
          )
        )
        .subscribe({
          next: (event: Partial<LimitedTimeEvent>) => {
            if (event) this.limitedTimeEventService.updateLimitedTimeEventById(event);
          },
          error: (err) => this.logger.error(err),
        });
    } else {
      if (challengesStatus.isGeneric) {
        this.closeModal();
      } else {
        this.goToGame(challengesStatus.gameId);
      }
    }
  }

  closeModal(): void {
    this.eventManager.closeModal(new Modal(NonPersistedModalName.MultiChallengesModalComponent, ModalType.View));
  }

  onExpiredChallenge(): void {
    this.eventManager.closeModal(new Modal(NonPersistedModalName.MultiChallengesModalComponent, ModalType.View));
    this.limitedTimeEventService.removeLimitedTimeEvent(this.gameChallenge()!.eventId);
  }

  private checkArrows(): void {
    if (!this.nativeService.isNative()) {
      setTimeout(() => {
        this.showArrows.set(
          this.multiChallengesElementSelector &&
            this.multiChallengesElementSelector.clientWidth > document.body.clientWidth
        );
      }, 500);
    }
  }

  private manageChallengeText(challenge: ChallengesStatuses): void {
    this.challengeText.push(this.limitedTimeEventService.getGameChallengeTransaltionKey(challenge));
  }
}
